import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getSupabase } from '@/hooks/useSupabase';
import type { User, Session } from '@supabase/supabase-js';

interface Profile {
  id: string;
  email: string;
  full_name: string;
  company: string;
  phone: string;
  country: string;
  role: string;
  created_at: string;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const supabase = getSupabase();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();

      // First try by ID
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (!error && data) {
        setProfile(data as Profile);
        return;
      }

      // Fallback: try by email
      if (authUser?.email) {
        const { data: dataByEmail, error: errByEmail } = await supabase
          .from('profiles')
          .select('*')
          .eq('email', authUser.email)
          .maybeSingle();

        if (!errByEmail && dataByEmail) {
          // Profile found by email but ID mismatch — update profile ID to match auth.uid()
          if ((dataByEmail as any).id !== userId) {
            await supabase
              .from('profiles')
              .update({ id: userId })
              .eq('email', authUser.email);
            (dataByEmail as any).id = userId;
          }
          setProfile(dataByEmail as Profile);
          return;
        }
      }

      // If still no profile but user exists, create one
      if (authUser?.email) {
        const { data: newProfile, error: insertErr } = await supabase
          .from('profiles')
          .insert({
            id: userId,
            email: authUser.email,
            full_name: authUser.user_metadata?.full_name || 'Admin',
            role: 'admin',
          })
          .select('*')
          .maybeSingle();

        if (!insertErr && newProfile) {
          setProfile(newProfile as Profile);
          return;
        }
      }
    } catch {
      // profile may not exist yet
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    // Get initial session - call setLoading(false) immediately after getSession
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (cancelled) return;
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setLoading(false);

      if (initialSession?.user) {
        fetchProfile(initialSession.user.id);
      } else {
        setProfileLoading(false);
      }
    }).catch((err) => {
      // Network or auth service unavailable — allow page to render anyway
      console.warn('[Auth] getSession failed:', err?.message || err);
      if (!cancelled) {
        setLoading(false);
        setProfileLoading(false);
      }
    });

    // Listen for auth changes - callback must be synchronous (no async)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (cancelled) return;
      setSession(newSession);
      setUser(newSession?.user ?? null);

      // Skip INITIAL_SESSION - getSession() above already handles initial load
      if (event === 'INITIAL_SESSION') return;

      if (newSession?.user) {
        setProfileLoading(true);
        fetchProfile(newSession.user.id);
      } else {
        setProfile(null);
        setProfileLoading(false);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setProfileLoading(false);
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, profileLoading, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}